import mqtt from 'mqtt'
import { createContext, useContext, useEffect, useRef, useState } from 'react'

// MQTT-only: this app never calls the backend. Dev default matches
// docker-compose.dev.yml's mosquitto-dev service (--profile live-sim);
// production is wss://mqtt.nhca.co.uk (see synology/mosquitto/README.md -
// anonymous, read-only listener, SWAG terminates real TLS in front of it).
const MQTT_URL = import.meta.env.VITE_MQTT_URL || 'ws://localhost:9001'
const TOPIC_PREFIX = import.meta.env.VITE_MQTT_TOPIC_PREFIX || 'nigan/timing'

const MqttContext = createContext(null)

export function MqttProvider({ children }) {
  const [topics, setTopics] = useState({})
  const [connected, setConnected] = useState(false)
  // Recent non-retained `hill` events (finished/flagged) - for a toast feed.
  // `latest` topic is deliberately not consumed for this - see
  // feedback_single_source_of_truth_points-adjacent design note: the
  // results grid is always-current and retained, so "latest" as a second
  // server-maintained list is redundant with a toast off the raw stream.
  const [toasts, setToasts] = useState([])
  // Every live subscriber to raw `hill` events (useHillEvents) - a mutable
  // ref, not React state, so a hill event doesn't force MqttProvider itself
  // to re-render; each listener decides its own state update. Parallel to
  // `toasts` above (which stays untouched) rather than a replacement for
  // it - `toasts`' rolling 5-item cap is fine for a toast feed but would
  // silently drop events a stateful consumer (e.g. hero-card tracking)
  // needs every one of, so that consumer gets its own un-capped fan-out.
  const hillListeners = useRef(new Set())

  useEffect(() => {
    const client = mqtt.connect(MQTT_URL, { reconnectPeriod: 2000 })

    client.on('connect', () => {
      setConnected(true)
      client.subscribe(`${TOPIC_PREFIX}/#`)
    })
    client.on('reconnect', () => setConnected(false))
    client.on('close', () => setConnected(false))
    client.on('error', () => {})

    client.on('message', (topic, payload) => {
      const name = topic.slice(TOPIC_PREFIX.length + 1)
      let data = null
      if (payload.length) {
        try {
          data = JSON.parse(payload.toString())
        } catch {
          return
        }
      }

      if (name === 'hill') {
        if (data) {
          setToasts((prev) => [...prev.slice(-4), { ...data, _id: `${Date.now()}-${Math.random()}` }])
          hillListeners.current.forEach((cb) => cb(data))
        }
        return
      }
      setTopics((prev) => ({ ...prev, [name]: data }))
    })

    return () => client.end(true)
  }, [])

  return (
    <MqttContext.Provider value={{ topics, connected, toasts, hillListeners }}>{children}</MqttContext.Provider>
  )
}

function useMqttContext() {
  const ctx = useContext(MqttContext)
  if (!ctx) throw new Error('Must be used within <MqttProvider>')
  return ctx
}

/** Latest retained payload for one topic (e.g. 'results', 'topten'), or null until it arrives. */
export function useMqttTopic(name) {
  return useMqttContext().topics[name] ?? null
}

export function useMqttConnected() {
  return useMqttContext().connected
}

/** Last few `hill` finished/flagged events, most recent last. */
export function useMqttToasts() {
  return useMqttContext().toasts
}

/**
 * Subscribe to every raw `hill` event (online/started/split/finished/
 * flagged) as it streams in - unlike useMqttToasts()'s rolling 5-item
 * feed, no event is ever silently dropped, so a burst of splits across
 * two riders can't evict an `online` a stateful consumer needed to see.
 * `onEvent` is called synchronously as each message arrives. The callback
 * doesn't need to be memoized by the caller - it's kept in a ref
 * internally, so passing an inline arrow function each render is fine.
 */
export function useHillEvents(onEvent) {
  const { hillListeners } = useMqttContext()
  const handlerRef = useRef(onEvent)
  handlerRef.current = onEvent

  useEffect(() => {
    const wrapped = (evt) => handlerRef.current(evt)
    hillListeners.current.add(wrapped)
    return () => hillListeners.current.delete(wrapped)
  }, [hillListeners])
}
